/**
 * Client-side port of `backend/common/public_email_domains.py`, for the registration
 * preview page only. Preview never submits to the API, so the backend check that the
 * live page relies on would never run and the seller's "block public email domains"
 * toggle would look inert there.
 *
 * Same two sources in the same order, same timeout, same parsing, same fail-closed
 * contract.
 */

import { addBreadcrumb, captureMessage } from "@sentry/react";

const HUBSPOT_CSV_URL =
  "https://f.hubspotusercontent40.net/hubfs/2832391/Marketing/Lead-Capture/free-domains-2.csv";
// Mirrors the default of the backend's PUBLIC_EMAIL_DOMAINS_FALLBACK_URL. That setting is
// env-overridable and this literal is not, so an override there has to be mirrored here.
const S3_FALLBACK_URL =
  "https://clazar-public-dev.s3.us-east-1.amazonaws.com/media/public/additional_metadata_files/free-domains.csv";

const FETCH_TIMEOUT_MS = 5000;
// Validation runs per keystroke, so a rejected load is remembered briefly — otherwise a
// down source costs one request pair per character typed.
const RETRY_AFTER_MS = 5000;

export class PublicEmailDomainsUnavailableError extends Error {
  constructor() {
    super("Public email-domain list unavailable from all sources");
    this.name = "PublicEmailDomainsUnavailableError";
  }
}

let cachedDomains: Promise<Set<string>> | null = null;
let failedAt = 0;

function parseCsv(text: string): Set<string> {
  const domains = new Set<string>();
  text.split(/\r?\n/).forEach((row) => {
    // Unquote before trimming, not after: `csv.reader` consumes the quotes first and Python's
    // `.strip()` then runs on the unquoted value, so `" gmail.com"` must yield `gmail.com`.
    // A cell padded *outside* its quotes is not a quoted cell to `csv.reader` either, so
    // ` "gmail.com" ` correctly keeps its quotes here too.
    const domain = row.split(",")[0].replace(/^"|"$/g, "").trim().toLowerCase();
    // The "." test is what skips blank lines and any header row.
    if (domain && domain.includes(".")) {
      domains.add(domain);
    }
  });
  return domains;
}

async function fetchDomains(url: string, sourceName: string): Promise<Set<string> | null> {
  // Browser fetch has no built-in timeout; without this a blackholed connection would
  // leave the field validating forever instead of falling through to the next source.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const domains = parseCsv(await response.text());
    if (!domains.size) {
      throw new Error("empty domain list");
    }
    return domains;
  } catch (error) {
    addBreadcrumb({
      category: "public_email_domains",
      level: "warning",
      message: "public_email_domains_source_failed",
      data: { sourceName, url, error: String(error) },
    });
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

async function loadDomains(): Promise<Set<string>> {
  const domains = await fetchDomains(HUBSPOT_CSV_URL, "hubspot");
  if (domains) {
    return domains;
  }
  const fallbackDomains = await fetchDomains(S3_FALLBACK_URL, "s3_fallback");
  if (fallbackDomains) {
    return fallbackDomains;
  }
  captureMessage("Public email-domain list unavailable from all sources", "error");
  throw new PublicEmailDomainsUnavailableError();
}

/** Resolve the set of known public/free email domains, fetched once per page load. */
export function getPublicEmailDomains(): Promise<Set<string>> {
  if (cachedDomains) {
    return cachedDomains;
  }
  if (failedAt && Date.now() - failedAt < RETRY_AFTER_MS) {
    return Promise.reject(new PublicEmailDomainsUnavailableError());
  }
  cachedDomains = loadDomains()
    .then((domains) => {
      failedAt = 0;
      return domains;
    })
    .catch((error) => {
      // Drop the rejected promise so a later keystroke retries, once the window has passed.
      cachedDomains = null;
      failedAt = Date.now();
      throw error;
    });
  return cachedDomains;
}

/**
 * Resolve true when `email`'s domain is a known public/free provider.
 *
 * Rejects with `PublicEmailDomainsUnavailableError` when no source is reachable, so
 * callers fail closed rather than letting the address through unverified.
 */
export async function isPublicEmailDomain(email: string): Promise<boolean> {
  if (!email || !email.includes("@")) {
    return false;
  }
  const domain = email
    .slice(email.lastIndexOf("@") + 1)
    .trim()
    .toLowerCase();
  const domains = await getPublicEmailDomains();
  return domains.has(domain);
}

/** Test seam: clears the module-level cache between cases. */
export function resetPublicEmailDomainsCache(): void {
  cachedDomains = null;
  failedAt = 0;
}
